import React, {
  PureComponent,
  ChangeEvent,
  KeyboardEvent,
  Suspense,
} from 'react';
import Loading from '@jg/loading';
import { Trans } from 'react-i18next';
import Observer from 'react-intersection-observer';
import CharacterCount from '@jg/character-count';
import { Button } from '@jg/react-tracking';
import addImage from '@jg/icons/lib/add-image.svg';
import classnames from 'classnames';
import ImageEditor, { OnEditChange } from '@jg/image-editor';
import AmountInput from '../AmountInput/AmountInput';
import makePhotoUrl from '../../utils/makePhotoUrl';
import { TEditTeamForm } from './EditTeamForm.d';
import { getCurrencySymbol, withTrack } from '../../utils';
import config from '../../config';
import FormError from '../FormError/FormError';
import Alerts from '../Alerts/Alerts';

const RichEditorWrapper = React.lazy(() => import('../Form/RichEditorWrapper'));

export class EditTeamForm extends PureComponent<TEditTeamForm> {
  state = {
    inView: false,
    displayAlerts: false,
  };

  handleButtonInView = (inView: boolean) => {
    this.setState({
      inView,
    });
  };

  onAmountInputChange = (
    event: Pick<ChangeEvent<HTMLInputElement>, 'target'>,
  ) => {
    const { setFieldValue } = this.props;
    setFieldValue('fundraisingTarget', event.target.value);
  };

  onImageEditorChange = (imageData: OnEditChange) => {
    const { setFieldValue, track } = this.props;
    setFieldValue('heroImage', imageData.base64 || '');
    track({
      action_name: 'click',
      subtype: 'button',
      event: 'click',
      event_value: 'change team cover photo',
      page_section: 'admin > edit > cover photo',
    });
  };

  preventEnterKey = (e: KeyboardEvent<HTMLTextAreaElement>) =>
    e.key === 'Enter' && e.preventDefault();

  render() {
    const {
      values: { heroImage, teamName, fundraisingTarget, story },
      errors,
      touched,
      handleChange,
      handleBlur,
      handleSubmit,
      isInFlight,
      isSuccess,
      isError,
      t,
      currencyCode,
      totalAmount,
      dirty,
    } = this.props;
    const { inView, displayAlerts } = this.state;

    const displayFundraisingTargetErrorMessage = Boolean(
      touched.fundraisingTarget && errors.fundraisingTarget,
    );
    const textareaClasses: string = classnames({
      'jg-form-control create-team__form ': true,
      'jg-form-control--with-error': touched.teamName && errors.teamName,
    });
    const isButtonDisabled: boolean = Boolean(
      errors.teamName || errors.fundraisingTarget || !dirty,
    );
    const totalAmountText = `${getCurrencySymbol(currencyCode)}${totalAmount} `;

    return (
      <form
        onSubmit={e => {
          handleSubmit(e);
          this.setState({ displayAlerts: true });
        }}
      >
        <div className="jg-input-wrapper jg-space-mbmd">
          <div className="create-team__input">
            <label
              className="jg-h3 jg-display-b jg-space-mbxs"
              htmlFor="jg-tc-create-team-input"
            >
              <Trans i18nKey="content.editteam.teamName" />
            </label>
            <textarea
              aria-invalid={Boolean(touched.teamName && errors.teamName)}
              id="jg-tc-create-team-input"
              className={textareaClasses}
              name="teamName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={teamName}
              onKeyPress={this.preventEnterKey}
              maxLength={100}
              aria-describedby="char-hint-info"
            />
            <CharacterCount value={teamName} maxLength={100}>
              {({ charactersRemaining }) => (
                <span
                  className="jg-form-control--with-character-count jg-text--small"
                  id="char-hint-info"
                  aria-live="polite"
                >
                  {charactersRemaining}
                  <span className="jg-sr-only">
                    <Trans
                      i18nKey="content.editteam.characters.remaining"
                      count={charactersRemaining}
                    />
                  </span>
                </span>
              )}
            </CharacterCount>
          </div>
          {touched.teamName &&
            errors.teamName && <FormError message={errors.teamName} />}
          <div className="jg-space-ptms">
            <AmountInput
              id="fundraisingTarget"
              name="fundraisingTarget"
              intlId="content.editteam.target"
              value={fundraisingTarget}
              defaultCurrency={currencyCode}
              onChange={this.onAmountInputChange}
              onBlur={handleBlur}
              hasValidationError={displayFundraisingTargetErrorMessage}
            />
            {displayFundraisingTargetErrorMessage && (
              <FormError message={errors.fundraisingTarget} />
            )}
            <span className="jg-text jg-space-mtxs">
              <Trans i18nKey="content.editteam.raisedsofar">
                <strong className="jg-text--brand-small jg-space-mtxs">
                  {{ totalAmountText }}
                </strong>
                raised so far
              </Trans>
            </span>
          </div>
          <div className="jg-input-wrapper jg-space-ptml">
            <h3 className="jg-space-mbxs">
              <Trans i18nKey="content.editteam.coverPhoto" />
            </h3>
            <ImageEditor
              mode="preview"
              image={makePhotoUrl(heroImage || config.defaultCoverPhoto)}
              editData={{ outputWidth: 1024, outputHeight: 576 }}
              showUploadButton
              onEditChange={this.onImageEditorChange}
              uploadButtonClass="jg-tc--upload-button"
              uploadButtonLabel={t('content.editteam.heroImage.uploadbutton')}
              imageType="image/png, image/jpeg"
              uploadIcon={addImage}
              sliderId="range-slider"
            />
            {touched.heroImage &&
              errors.heroImage && <FormError message={errors.heroImage} />}
          </div>
          <div className="jg-input-wrapper jg-space-mbmd jg-space-mtml">
            <label className="jg-h3 jg-display-b jg-space-mbxs" htmlFor="story">
              <Trans i18nKey="content.editteam.description.title" />
            </label>
            {story && (
              <Suspense
                fallback={<div className="richeditor--loading">Loading...</div>}
              >
                <RichEditorWrapper
                  id="story"
                  {...this.props}
                  placeholder={t('content.editteam.description.placeholder')}
                  storyValue={story}
                  incompatibleRichContentMessage={t(
                    'content.editteam.incompatiblerichcontent',
                  )}
                />
              </Suspense>
            )}
            {touched.story &&
              errors.story && <FormError message={errors.story} />}
          </div>
        </div>
        <Observer onChange={this.handleButtonInView} />
        <div className={!inView ? 'jg-tc-button__fixed' : ''}>
          <div className="jg-tc-button__fixed--container">
            <div className="jg-tc-button-container jg-space-mtml">
              <Button
                id="jg-tc-create-team-continue-button"
                disabled={isButtonDisabled}
                type="submit"
                className="jg-btn jg-space-mbmd"
                trackingData={{
                  action_name: 'click',
                  subtype: 'button',
                  event: 'click',
                  event_value: 'save changes',
                  page_section: 'admin > edit > team story',
                }}
              >
                {isInFlight ? (
                  <Loading size="small" colour="white" />
                ) : (
                  <Trans i18nKey="content.editteam.button" />
                )}
              </Button>
            </div>
            {displayAlerts && (
              <Alerts isError={isError} isSuccess={isSuccess} />
            )}
          </div>
        </div>
      </form>
    );
  }
}

export default withTrack(EditTeamForm);
